import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, type AccountPasswordResult } from "../api/client";
import { queryKeys } from "../api/queryKeys";
import { useTheme } from "../contexts/ThemeContext";
import { CenteredMessage } from "../components/CenteredMessage";
import { AccountDetail } from "../components/AccountDetail";
import { CreateAccountPanel } from "../components/CreateAccountPanel";
import { EditAccountPanel } from "../components/EditAccountPanel";

/**
 * Écran maître-détail des comptes API : liste à gauche (nom + entreprise),
 * détail du compte sélectionné à droite (infos, mot de passe, onglets de droits).
 */
export default function AccountsPage() {
  const { t } = useTheme();

  // État d'UI : identifiants uniquement, jamais d'objet entier.
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(false);
  // Mot de passe fraîchement généré (création ou régénération), affiché une seule
  // fois dans le bandeau du détail, jamais conservé ailleurs.
  const [revealed, setRevealed] = useState<AccountPasswordResult | null>(null);

  const accountsQ = useQuery({ queryKey: queryKeys.accounts, queryFn: api.accounts.list });

  const handleSelect = (idUserAPI: number) => {
    setCreating(false);
    setSelectedId(idUserAPI);
    setEditing(false);
    if (revealed && revealed.idUserAPI !== idUserAPI) setRevealed(null);
  };

  const handleCreated = (result: AccountPasswordResult) => {
    setCreating(false);
    setEditing(false);
    setSelectedId(result.idUserAPI);
    setRevealed(result);
  };

  if (accountsQ.isLoading) return <CenteredMessage>Chargement des comptes…</CenteredMessage>;
  if (accountsQ.isError)
    return <CenteredMessage tone="error">Impossible de charger les comptes : {(accountsQ.error as Error).message}</CenteredMessage>;

  const accounts = accountsQ.data!;
  const selected = selectedId != null ? accounts.find(a => a.idUserAPI === selectedId) : undefined;

  // Comptes V2 en tête, puis alphabétique ; filtre sur login, nom et entreprise.
  const needle = search.trim().toLowerCase();
  const shown = accounts
    .filter(a =>
      !needle
      || a.login.toLowerCase().includes(needle)
      || a.nom.toLowerCase().includes(needle)
      || (a.nomEntreprise ?? "").toLowerCase().includes(needle))
    .sort((a, b) =>
      Number(b.hasV2Access) - Number(a.hasV2Access)
      || a.login.localeCompare(b.login, "fr", { sensitivity: "base" }));

  const activeCount = accounts.filter(a => a.hasV2Access).length;

  return (
    <div style={{ display: "flex", flex: 1, minWidth: 0 }}>
      {/* LISTE (maître) */}
      <aside style={{
        width: 300, flex: "none", borderRight: `1px solid ${t.divider}`,
        display: "flex", flexDirection: "column", minHeight: 0,
      }}>
        <div style={{ padding: "14px 14px 10px", borderBottom: `1px solid ${t.divider}` }}>
          <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14.5, fontWeight: 600, color: t.text1 }}>Comptes API</div>
              <div style={{ fontSize: 11.5, color: t.text2 }}>
                {accounts.length} compte{accounts.length > 1 ? "s" : ""}, {activeCount} activé{activeCount > 1 ? "s" : ""} V2
              </div>
            </div>
            <button onClick={() => { setCreating(true); setEditing(false); setSelectedId(null); }} title="Créer un compte" style={{
              border: "none", background: t.btnBg, color: "#fff", borderRadius: 8,
              padding: "7px 12px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
            }}>+ Créer</button>
          </div>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher…"
            style={{
              width: "100%", padding: "8px 12px", borderRadius: 8,
              border: `1px solid ${t.inputBorder}`, background: t.inputBg, color: t.text1,
              fontSize: 13, fontFamily: "inherit", outline: "none",
            }}
          />
        </div>

        <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
          {shown.length === 0 && (
            <div style={{ padding: 16, fontSize: 12.5, color: t.text2 }}>
              Aucun compte ne correspond à « {search} ».
            </div>
          )}
          {shown.map(a => {
            const active = a.idUserAPI === selectedId;
            return (
              <div
                key={a.idUserAPI}
                onClick={() => handleSelect(a.idUserAPI)}
                style={{
                  padding: "10px 14px", cursor: "pointer",
                  background: active ? t.rowSel : "transparent",
                  borderLeft: `3px solid ${active ? t.accent : "transparent"}`,
                  borderBottom: `1px solid ${t.rowBorder}`,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ flex: 1, fontSize: 13.5, fontWeight: 600, color: t.text1 }}>{a.nom}</span>
                  {a.hasV2Access && (
                    <span style={{
                      padding: "1px 8px", borderRadius: 20, fontSize: 10.5, fontWeight: 700,
                      background: t.doneBg, color: t.doneColor, whiteSpace: "nowrap",
                    }}>V2</span>
                  )}
                </div>
                <div style={{ fontSize: 11.5, color: t.text2, marginTop: 2 }}>
                  {/* Plus d'id en repli : celui d'un compte non rattaché renvoie à
                      wapagri.Entreprise, table abandonnée le 09/09/2026, donc trompeur. */}
                  {a.nomEntreprise ?? "Société non rattachée"}
                </div>
              </div>
            );
          })}
        </div>
      </aside>

      {/* DÉTAIL */}
      <section style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", minHeight: 0 }}>
        {creating ? (
          <CreateAccountPanel onCreated={handleCreated} onCancel={() => setCreating(false)} />
        ) : selected && editing ? (
          <EditAccountPanel
            account={selected}
            onSaved={() => setEditing(false)}
            onCancel={() => setEditing(false)}
          />
        ) : selected ? (
          <AccountDetail
            account={selected}
            revealed={revealed?.idUserAPI === selected.idUserAPI ? revealed : null}
            onReveal={setRevealed}
            onDismissReveal={() => setRevealed(null)}
            onEdit={() => setEditing(true)}
          />
        ) : (
          <CenteredMessage>Sélectionnez un compte dans la liste, ou créez-en un.</CenteredMessage>
        )}
      </section>
    </div>
  );
}
